"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";

const services = [
{
  id: "cleaning",
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>,

  tag: "Most Popular",
  title: "Airbnb Turnover Cleaning",
  description: "Hotel-level 5-star turnovers between every guest. Linen replacement, full restocking, staging — done in hours, not days.",
  features: ["Linen & towel replacement", "Toiletry restock", "Full appliance cleaning", "Guest-ready staging"],
  startingAt: "$125",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1344ade2b-1772226463656.png",
  imageAlt: "Professional cleaner preparing luxury vacation rental bedroom with fresh white linens",
  href: "/services#cleaning",
  color: "from-navy to-navy-light"
},
{
  id: "management",
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>,

  tag: "Full Service",
  title: "Short-Term Rental Management",
  description: "We handle everything — listing optimization, dynamic pricing, 24/7 guest messaging, and monthly owner reports.",
  features: ["Dynamic pricing strategy", "24/7 guest communication", "Maintenance coordination", "Monthly owner reports"],
  startingAt: "18%",
  startingLabel: "of gross revenue",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_117cebfc4-1772226463013.png",
  imageAlt: "Property manager reviewing vacation rental analytics dashboard on laptop",
  href: "/services#management",
  color: "from-gold-dark to-gold"
}];


export default function ServicesPreview() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef?.current?.querySelectorAll(".reveal-hidden");
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="section-padding bg-sand">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <span className="inline-block text-gold text-xs font-bold uppercase tracking-widest mb-4">
            What We Do
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-5">
            Two Services,{" "}
            <span className="italic font-light text-gradient-gold">One Standard</span>
          </h2>
          <p className="text-navy/60 text-lg max-w-2xl mx-auto leading-relaxed">
            Whether you need spotless turnovers or hands-off property management, Premier delivers hotel-quality results every time.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services?.map((service, i) =>
          <div
            key={service?.id}
            className={`card-luxury overflow-hidden reveal-hidden group`}
            style={{ transitionDelay: `${i * 150}ms` }}>
            
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <AppImage
                src={service?.image}
                alt={service?.imageAlt}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-700" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/60 to-transparent" />
                {/* Tag */}
                <div className="absolute top-4 left-4">
                  <span className="btn-gold px-3.5 py-1.5 rounded-full text-xs font-bold">
                    {service?.tag}
                  </span>
                </div>
                {/* Price */}
                <div className="absolute bottom-4 left-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-white font-heading text-3xl font-bold">{service?.startingAt}</span>
                    <span className="text-white/70 text-sm">
                      {service?.startingLabel ?? "starting per clean"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-navy/8 flex items-center justify-center text-navy">
                    {service?.icon}
                  </div>
                  <h3 className="font-heading text-xl text-navy">{service?.title}</h3>
                </div>
                <p className="text-navy/60 text-sm leading-relaxed mb-5">{service?.description}</p>

                {/* Features */}
                <ul className="grid grid-cols-2 gap-2 mb-6">
                  {service?.features?.map((f) =>
                <li key={f} className="flex items-center gap-2 text-sm text-navy/70">
                      <svg className="w-4 h-4 text-gold shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {f}
                    </li>
                )}
                </ul>

                <Link
                href={service?.href}
                className="btn-navy w-full py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 group/btn">
                
                  Learn More
                  <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 reveal-hidden">
          <Link
            href="/services"
            className="inline-flex items-center gap-2 text-navy font-semibold text-sm border-b-2 border-gold pb-0.5 hover:text-gold transition-colors duration-300">
            
            View Full Pricing & Service Details
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>);

}